/**
 * G11. Работа: «Смена». Раз в пять лет работы (`flags.jobStartAge`) —
 * 15-секундная игра по типу работы: поле («собрать урожай»: тапы по
 * спелым клеткам 3×3; уровень 0 в стране с высокой долей занятых в
 * сельском хозяйстве), завод («конвейер»: тап в ритм; уровень 0), торговля
 * («сдача»: быстрый счёт, три суммы; уровень 1), офис («сортировка»: шесть
 * карточек в две стопки; уровни 2–3). Точность > SHIFT_SHARP_ACCURACY —
 * шанс повышения в этом году +10 % (`flags.promotionBonus`, читает
 * экономика), точность < SHIFT_TIRED_ACCURACY — здоровье −1.
 * «Пропустить» — обычная смена без последствий.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { getValue } from '@life/data';
import { FLAG_JOB_START_AGE, FLAG_PROMOTION_BONUS, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { shuffle } from '../skill/common';
import { FIELD_MIN_AGRI_SHARE } from '../skill/field';
import { beatHits, distinctIndexes, nothing, numberFlag } from './common';

export const SHIFT_EVERY_YEARS = 5;
export const SHIFT_MIN_AGE = 15;
export const SHIFT_MAX_AGE = 80;
export const SHIFT_DURATION_MS = 15_000;
export const SHIFT_KINDS = ['field', 'factory', 'office', 'trade'] as const;
export type ShiftKind = (typeof SHIFT_KINDS)[number];
export const SHIFT_SHARP_ACCURACY = 0.7;
export const SHIFT_TIRED_ACCURACY = 0.4;
export const SHIFT_PROMOTION_BONUS = 0.1;
export const SHIFT_TIRED_HEALTH = -1;
/** Поле: клетки 3×3, спелых — от 3 до 6. */
export const SHIFT_FIELD_CELLS = 9;
export const SHIFT_FIELD_RIPE_MIN = 3;
export const SHIFT_FIELD_RIPE_MAX = 6;
/** Завод: 6 ударов, первый через 2 с, затем каждые 2 с; окно ±300 мс. */
export const SHIFT_FACTORY_BEATS = 6;
export const SHIFT_FACTORY_FIRST_BEAT_MS = 2000;
export const SHIFT_FACTORY_BEAT_MS = 2000;
export const SHIFT_FACTORY_WINDOW_MS = 300;
/** Офис: шесть карточек, по три в каждую из двух стопок. */
export const SHIFT_OFFICE_CARDS = 6;
export const SHIFT_OFFICE_STACKS = ['money', 'papers'] as const;
/** Карточки офиса: ключ → стопка. */
export const SHIFT_OFFICE_BANK: readonly { key: string; stack: number }[] = [
  { key: 'minigame.shift.card.invoice', stack: 0 },
  { key: 'minigame.shift.card.receipt', stack: 0 },
  { key: 'minigame.shift.card.payroll', stack: 0 },
  { key: 'minigame.shift.card.letter', stack: 1 },
  { key: 'minigame.shift.card.memo', stack: 1 },
  { key: 'minigame.shift.card.request', stack: 1 },
];
/** Торговля: три суммы, цена 11..99, оплата круглой купюрой, три варианта сдачи. */
export const SHIFT_TRADE_SUMS = 3;
export const SHIFT_TRADE_OPTIONS = 3;

export interface ShiftCard {
  key: string;
  /** Верная стопка 0|1. */
  stack: number;
}

export interface ShiftSum {
  price: number;
  paid: number;
  options: number[];
  /** Индекс верной сдачи в `options`. */
  correct: number;
}

export interface ShiftState {
  kind: ShiftKind;
  durationMs: number;
  /** Поле: спелая ли клетка. Пусто у других видов. */
  cells: boolean[];
  /** Завод: моменты ударов (мс от старта) и окно. */
  beats: number[];
  window: number;
  /** Офис: карточки и ключи названий стопок. */
  cards: ShiftCard[];
  stacks: string[];
  /** Торговля: суммы. */
  sums: ShiftSum[];
}

/** Поле/офис/торговля — `picks` (индексы клеток / стопка на карточку / вариант на сумму), завод — `taps`. */
export type ShiftInput = { picks?: number[]; taps?: number[] };

function agriShare(ds: Dataset, life: Life): number {
  try {
    return getValue(ds, life.iso3, 'agriShare', life.birthYear + life.age).v;
  } catch {
    return 0;
  }
}

/** Тип смены по уровню работы и доле занятых в сельском хозяйстве. */
export function shiftKind(life: Life, ds: Dataset): ShiftKind {
  const level = life.job?.level ?? 0;
  if (level <= 0) return agriShare(ds, life) > FIELD_MIN_AGRI_SHARE ? 'field' : 'factory';
  return level === 1 ? 'trade' : 'office';
}

/** Сколько лет на нынешней работе, или null без работы/флага. */
export function shiftYearsWorked(life: Life): number | null {
  if (life.job === null || typeof life.flags[FLAG_JOB_START_AGE] !== 'number') return null;
  return life.age - numberFlag(life, FLAG_JOB_START_AGE);
}

/** Точность 0..1 по виду смены и вводу. */
export function shiftAccuracy(state: ShiftState, input: ShiftInput): number {
  switch (state.kind) {
    case 'field': {
      const ripe = state.cells.filter(Boolean).length;
      if (ripe === 0) return 0;
      const picks = distinctIndexes(input.picks, state.cells.length);
      const hits = picks.filter((i) => state.cells[i]).length;
      const wrong = picks.length - hits;
      return Math.max(0, hits - wrong) / ripe;
    }
    case 'factory':
      return state.beats.length === 0 ? 0 : beatHits(state.beats, state.window, input.taps ?? []) / state.beats.length;
    case 'office': {
      const picks = Array.isArray(input.picks) ? input.picks : [];
      const right = state.cards.filter((c, i) => picks[i] === c.stack).length;
      return state.cards.length === 0 ? 0 : right / state.cards.length;
    }
    case 'trade': {
      const picks = Array.isArray(input.picks) ? input.picks : [];
      const right = state.sums.filter((s, i) => picks[i] === s.correct).length;
      return state.sums.length === 0 ? 0 : right / state.sums.length;
    }
  }
}

function result(accuracy: number): MinigameOutcome {
  const pct = Math.round(accuracy * 100);
  if (accuracy > SHIFT_SHARP_ACCURACY) {
    return { ...nothing(), flags: { [FLAG_PROMOTION_BONUS]: SHIFT_PROMOTION_BONUS }, logKey: 'minigame.shift.sharp', logParams: { accuracy: pct } };
  }
  if (accuracy < SHIFT_TIRED_ACCURACY) {
    return { ...nothing(), stats: { health: SHIFT_TIRED_HEALTH }, logKey: 'minigame.shift.tired', logParams: { accuracy: pct } };
  }
  return { ...nothing(), logKey: 'minigame.shift.done', logParams: { accuracy: pct } };
}

function tradeSum(rng: Rng): ShiftSum {
  const price = rng.int(11, 99);
  const paid = price <= 50 ? 100 : 200;
  const change = paid - price;
  const wrongs = new Set<number>();
  while (wrongs.size < SHIFT_TRADE_OPTIONS - 1) {
    const w = change + rng.pick([-10, -5, -1, 1, 5, 10]);
    if (w > 0 && w !== change) wrongs.add(w);
  }
  const options = shuffle([change, ...wrongs], rng);
  return { price, paid, options, correct: options.indexOf(change) };
}

export const shift: MinigameDef<ShiftInput, ShiftState> = {
  id: 'shift',
  section: 'G',
  triggers: [{ kind: 'age', min: SHIFT_MIN_AGE, max: SHIFT_MAX_AGE, everyYears: 1 }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    const worked = shiftYearsWorked(life);
    return worked !== null && worked >= SHIFT_EVERY_YEARS && worked % SHIFT_EVERY_YEARS === 0;
  },
  setup(life: Life, ds: Dataset, rng: Rng): ShiftState {
    const kind = shiftKind(life, ds);
    const state: ShiftState = { kind, durationMs: SHIFT_DURATION_MS, cells: [], beats: [], window: 0, cards: [], stacks: [], sums: [] };
    switch (kind) {
      case 'field': {
        const ripe = rng.int(SHIFT_FIELD_RIPE_MIN, SHIFT_FIELD_RIPE_MAX);
        const order = shuffle(Array.from({ length: SHIFT_FIELD_CELLS }, (_x, i) => i), rng);
        state.cells = Array.from({ length: SHIFT_FIELD_CELLS }, (_x, i) => order.indexOf(i) < ripe);
        break;
      }
      case 'factory':
        state.beats = Array.from({ length: SHIFT_FACTORY_BEATS }, (_x, i) => SHIFT_FACTORY_FIRST_BEAT_MS + i * SHIFT_FACTORY_BEAT_MS);
        state.window = SHIFT_FACTORY_WINDOW_MS;
        break;
      case 'office':
        state.cards = shuffle(SHIFT_OFFICE_BANK, rng).map((c) => ({ key: c.key, stack: c.stack }));
        state.stacks = SHIFT_OFFICE_STACKS.map((s) => `minigame.shift.stack.${s}`);
        break;
      case 'trade':
        state.sums = Array.from({ length: SHIFT_TRADE_SUMS }, () => tradeSum(rng));
        break;
    }
    return state;
  },
  play(state: ShiftState, input: ShiftInput): MinigameOutcome {
    return result(shiftAccuracy(state, input));
  },
  expected(): MinigameOutcome {
    return { ...nothing(), logKey: 'minigame.shift.done', logParams: { accuracy: Math.round(((SHIFT_SHARP_ACCURACY + SHIFT_TIRED_ACCURACY) / 2) * 100) } };
  },
};
